<template>
  <el-dialog class="page-dialog center-dialog" :title="dialogTitle" :visible="visible" width="600px" :close-on-click-modal="false" @close="handleClose">
    <div class="dialog-content">
      <el-form ref="formRef" :model="localFormData" :rules="formRules" label-width="80px" size="small">
        <el-form-item label="分类名称" prop="type_name">
          <LanguageInput
            v-model="localFormData.type_name"
            :lang.sync="localFormData.type_name_lang"
            placeholder="请输入分类名称"
            :maxlength="50"
            :show-word-limit="true"
            clearable
            style="width: 420px"
          />
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="localFormData.sort" :min="0" :max="9999" controls-position="right" class="sort-input" />
        </el-form-item>
        <el-form-item label="图标" prop="icon_url">
          <SingleImageUpload v-model="localFormData.icon_url" :size="100" />
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="loading" @click="handleConfirm">确认</el-button>
    </div>
  </el-dialog>
</template>

<script setup>
import { addFaqCategory, updateFaqCategory } from '@/api/content/faq'
import SingleImageUpload from '@/components/Upload/SingleImageUpload.vue'
import LanguageInput from '@/components/LanguageInput/index.vue'
import config from '@/config'
import { defineEmits, defineProps, getCurrentInstance, reactive, ref, watch } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  dialogTitle: {
    type: String,
    default: '新增分类',
  },
  formData: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['update:visible', 'success'])

const { proxy } = getCurrentInstance()
const formRef = ref(null)
const loading = ref(false)

const langOptions = Array.isArray(config.languages) ? config.languages : []
const defaultLang = langOptions[0]?.value || 'EN'

function buildLangObj(initialValue = '') {
  const obj = {}
  for (let i = 0; i < langOptions.length; i += 1) {
    const k = langOptions[i]?.value
    if (k != null) obj[k] = initialValue
  }
  return obj
}

const localFormData = reactive({
  id: null,
  type_name: buildLangObj(''),
  type_name_lang: defaultLang,
  sort: 0,
  icon_url: '',
})

const formRules = {
  type_name: [
    {
      validator: (rule, value, callback) => {
        const current = localFormData.type_name?.[localFormData.type_name_lang]
        if (!current) {
          callback(new Error('请输入分类名称'))
          return
        }
        callback()
      },
      trigger: 'blur',
    },
  ],
  sort: [{ required: true, message: '请输入排序', trigger: 'blur' }],
}

watch(
  () => [props.visible, props.formData],
  () => {
    if (props.visible) {
      const emptyNameObj = buildLangObj('')
      const raw = props.formData?.type_name
      const nameObj =
        raw && typeof raw === 'object' && !Array.isArray(raw)
          ? { ...emptyNameObj, ...raw }
          : { ...emptyNameObj, [localFormData.type_name_lang]: raw || '' }

      Object.assign(localFormData, {
        id: props.formData.id ?? null,
        type_name: nameObj,
        type_name_lang: localFormData.type_name_lang || defaultLang,
        sort: props.formData.sort ?? 0,
        icon_url: props.formData.icon_url ?? '',
      })
    }
  },
  { immediate: true }
)

function handleClose() {
  emit('update:visible', false)
}

function handleConfirm() {
  formRef.value.validate(valid => {
    if (!valid) return
    loading.value = true
    const api = localFormData.id ? updateFaqCategory : addFaqCategory
    const payload = {
      type_name: localFormData.type_name,
      sort: localFormData.sort,
      icon_url: localFormData.icon_url,
    }
    if (localFormData.id) payload.id = localFormData.id
    api(payload)
      .then(res => {
        if (res && res.code !== undefined && res.code !== 0) {
          throw new Error(res.message || '保存失败')
        }
        proxy.$message.success('保存成功')
        emit('success')
        handleClose()
      })
      .catch(error => {
        console.error('保存分类失败', error)
        proxy.$message.error(error?.response?.data?.message || error.message || '保存分类失败')
      })
      .finally(() => {
        loading.value = false
      })
  })
}
</script>

<style lang="scss" scoped>
.dialog-fixed-height {
  display: flex;
  align-items: center;
  justify-content: center;
}
.dialog-fixed-height ::v-deep .el-dialog {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  margin: auto;
}
.dialog-fixed-height ::v-deep .el-dialog__header {
  flex-shrink: 0;
}
.dialog-fixed-height ::v-deep .el-dialog__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.dialog-fixed-height ::v-deep .el-dialog__footer {
  flex-shrink: 0;
}
.sort-input {
  width: 120px;
}
::v-deep .sort-input.el-input-number {
  width: 120px;
}
</style>
